<script setup>
import { computed } from 'vue';
import Commons from '../../shared/libraries/commons';

const props = defineProps({
  match: {
    required: true,
    type: Object
  }
});

const bestBet = computed(() => {
  const allOdds = props.match?.allOdds;
  if (!allOdds) return null;

  const titles = {
    1: `Victoire ${props.match?.team1?.name}`,
    2: `Victoire ${props.match?.team2?.name}`,
    4: `${props.match?.team1?.name} ou Nul`,
    5: `${props.match?.team2?.name} ou Nul`,
    6: 'Double chance 12',
    7: '+2.5 buts',
    8: '-2.5 buts',
    9: 'Les 2 marquent',
    10: 'Pas les 2'
  };

  const homeWinProb = parseFloat(allOdds['1']?.probability) || 0;
  const awayWinProb = parseFloat(allOdds['2']?.probability) || 0;

  let best = null;
  let bestType = null;

  if (homeWinProb >= 50 || awayWinProb >= 50) {
    if (homeWinProb >= awayWinProb) {
      best = allOdds['1'];
      bestType = '1';
    } else {
      best = allOdds['2'];
      bestType = '2';
    }
  } else {
    const otherTypes = ['4', '5', '6', '7', '8', '9', '10'];
    let highestProb = 0;

    for (const type of otherTypes) {
      const odd = allOdds[type];
      if (odd && odd.probability) {
        const prob = parseFloat(odd.probability);
        if (prob > highestProb) {
          highestProb = prob;
          best = odd;
          bestType = type;
        }
      }
    }
  }

  if (!best) return null;

  return {
    title: titles[bestType] || best.description,
    value: best.value,
    probability: best.probability
  };
});
</script>

<template>
  <div
    class="bg-gray-50 rounded-2xl overflow-hidden h-full flex flex-col border border-neutral-200"
    @click.stop="$bus.emit($constants.EVENTS.MATCH.SHOW_MATCH_DETAIL, match)"
  >
    <!-- header bar with date -->
    <div class="bg-gradient-to-r from-primary to-primary/80 px-4 py-2">
      <div class="flex items-center justify-between">
        <div class="text-white/90 text-xs font-medium">
          {{ Commons.time(match?.date, 'dddd D MMM') }}
        </div>
        <div class="text-white text-xs font-semibold">
          {{ Commons.time(match?.date, 'HH:mm') }}
        </div>
      </div>
    </div>

    <!-- teams section -->
    <div class="flex-1 p-4 flex flex-col justify-center">
      <!-- team 1 -->
      <div class="flex items-center gap-3 mb-4">
        <div class="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
          <ImgComponent
            :src="match?.team1?.logo"
            fallback="/images/match/t-shirt.svg"
            class="w-8 h-8 object-contain"
            alt=""
          />
        </div>
        <div class="flex-1 min-w-0">
          <div class="font-semibold text-gray-900 truncate">
            {{ match?.team1?.name ?? '' }}
          </div>
        </div>
      </div>

      <!-- divider -->
      <div class="flex items-center gap-2 my-3">
        <div class="flex-1 h-px bg-gray-200"></div>
        <div class="text-xs font-bold text-gray-400 px-2">MATCH</div>
        <div class="flex-1 h-px bg-gray-200"></div>
      </div>

      <!-- team 2 -->
      <div class="flex items-center gap-3 mt-4">
        <div class="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
          <ImgComponent
            :src="match?.team2?.logo"
            fallback="/images/match/t-shirt.svg"
            class="w-8 h-8 object-contain"
            alt=""
          />
        </div>
        <div class="flex-1 min-w-0">
          <div class="font-semibold text-gray-900 truncate">
            {{ match?.team2?.name ?? '' }}
          </div>
        </div>
      </div>
    </div>

    <!-- prediction section -->
    <div
      class="p-4 bg-gray-50 border-t border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors"
      @click.stop="
        $bus.emit($constants.EVENTS.SERVICES.START, {
          group: 'recharges',
          option: 'fund'
        })
      "
    >
      <div class="flex items-center justify-between">
        <div class="flex-1">
          <div class="text-xs text-gray-500 mb-1">Pronostic recommandé</div>
          <div class="font-semibold text-gray-900 text-sm">
            {{ bestBet?.title }}
          </div>
        </div>
        <div class="flex flex-col items-end ml-3">
          <div class="text-xl font-bold text-primary">
            {{ bestBet?.probability }}%
          </div>
          <div class="text-xs text-gray-500">{{ bestBet?.value }}</div>
        </div>
      </div>

      <!-- cta -->
      <div class="mt-3 flex items-center gap-2 text-primary text-sm font-medium">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <path d="M12 5v14M5 12h14"/>
        </svg>
        <span>Déposer maintenant</span>
      </div>
    </div>
  </div>
</template>
